import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { PlusIcon, MinusIcon } from 'lucide-react';
interface FAQItem {
  question: string;
  answer: string | string[];
}
export function FAQ() {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });
  const [openIndex, setOpenIndex] = useState<number | null>(0);
  const faqs: FAQItem[] = [{
    question: 'How many months does a session cover?',
    answer: "Each session lasts 2 months, from the beginner's level to the Advanced level."
  }, {
    question: 'Do you accept installment payment?',
    answer: "No, we don't accept installment payments."
  }, {
    question: 'Are there recordings of each class?',
    answer: 'Yes, there are, but they are available only on demand.'
  }, {
    question: 'How do I know the right level to register for?',
    answer: 'You can take the language test: TCF - Test de connaissance du français en ligne with TV5MONDE'
  }, {
    question: 'Is the placement test required?',
    answer: 'If you wish to know the right level for you, rather than just the number of years you have studied French, then the placement test is highly recommended.'
  }, {
    question: 'Who are the classes meant for?',
    answer: ['Individuals who wish to write the TEF/TCF Exam to increase their scores as they plan to immigrate to Canada.', 'Individuals who want to leverage the language for their jobs.', 'Companies that wish to register their employees so they can become bilingual.']
  }, {
    question: 'Who are the course instructors?',
    answer: 'Our instructors for all languages are well-experienced and highly qualified. They have trained and prepared candidates for the TCF/TEF Exam. They have at least 5 years of teaching experience with DELF/DALF Certifications.'
  }];
  const toggleFAQ = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };
  return <section className="py-24 bg-white" data-id="element-173">
      <div className="max-w-7xl mx-auto px-6" data-id="element-174">
        <motion.div ref={ref} initial={{
        opacity: 0,
        y: 30
      }} animate={inView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.6
      }} className="text-center mb-16" data-id="element-175">
          <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4" data-id="element-176">
            Frequently Asked <span className="text-green-600" data-id="element-177">Questions</span>
          </h2>
          <p className="text-xl text-slate-600" data-id="element-178">
            Find answers to common questions about our language programs
          </p>
        </motion.div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start" data-id="element-179">
          <div className="space-y-4" data-id="element-180">
            {faqs.map((faq, index) => <motion.div key={index} initial={{
            opacity: 0,
            x: -30
          }} animate={inView ? {
            opacity: 1,
            x: 0
          } : {}} transition={{
            duration: 0.4,
            delay: index * 0.1
          }} data-id="element-181">
                <div className={`bg-white rounded-2xl shadow-lg overflow-hidden transition-all duration-300 ${openIndex === index ? 'ring-2 ring-green-600' : ''}`} data-id="element-182">
                  <button onClick={() => toggleFAQ(index)} className="w-full px-6 py-5 flex items-center justify-between text-left hover:bg-green-50 transition-colors duration-200" data-id="element-183">
                    <span className="text-base font-semibold text-slate-900 pr-4" data-id="element-184">
                      {faq.question}
                    </span>
                    <motion.div animate={{
                  rotate: openIndex === index ? 180 : 0
                }} transition={{
                  duration: 0.3
                }} className="flex-shrink-0" data-id="element-185">
                      {openIndex === index ? <MinusIcon className="text-green-600" size={24} data-id="element-186" /> : <PlusIcon className="text-slate-400" size={24} data-id="element-187" />}
                    </motion.div>
                  </button>
                  <AnimatePresence data-id="element-188">
                    {openIndex === index && <motion.div initial={{
                  height: 0,
                  opacity: 0
                }} animate={{
                  height: 'auto',
                  opacity: 1
                }} exit={{
                  height: 0,
                  opacity: 0
                }} transition={{
                  duration: 0.3
                }} data-id="element-189">
                        <div className="px-6 pb-5 pt-2" data-id="element-190">
                          <div className="border-t border-slate-200 pt-4" data-id="element-191">
                            {Array.isArray(faq.answer) ? <ul className="space-y-3" data-id="element-192">
                                {faq.answer.map((item, i) => <li key={i} className="text-slate-600 flex items-start gap-3" data-id="element-193">
                                    <span className="w-2 h-2 bg-green-600 rounded-full mt-2 flex-shrink-0" data-id="element-194" />
                                    <span data-id="element-195">{item}</span>
                                  </li>)}
                              </ul> : <p className="text-slate-600 leading-relaxed" data-id="element-196">
                                {faq.answer}
                              </p>}
                          </div>
                        </div>
                      </motion.div>}
                  </AnimatePresence>
                </div>
              </motion.div>)}
          </div>
          <motion.div initial={{
          opacity: 0,
          x: 30
        }} animate={inView ? {
          opacity: 1,
          x: 0
        } : {}} transition={{
          duration: 0.6,
          delay: 0.3
        }} className="sticky top-24" data-id="element-197">
            <img src="https://images.unsplash.com/photo-1502602898657-3e91760cbb34?w=800&auto=format&fit=crop" alt="Eiffel Tower, Paris" className="w-full h-auto rounded-3xl shadow-2xl" data-id="element-198" />
            <div className="mt-6 text-center" data-id="element-199">
              <h3 className="text-2xl font-bold text-slate-900 mb-2" data-id="element-200">
                Still have questions?
              </h3>
              <p className="text-slate-600 mb-4" data-id="element-201">
                Our team is here to help you
              </p>
              <a href="#contact" className="inline-block bg-green-600 text-white px-8 py-3 rounded-full font-semibold hover:bg-green-700 transition-colors duration-200" data-id="element-202">
                Contact Us
              </a>
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}